// ── Listing Feature Initialization Isolation ──────────────────────
//
// A single feature failing during page bootstrap must not keep later
// independent features from binding their UI. This helper intentionally keeps
// the existing synchronous start order while also observing returned promises.

function logFailure(logger, message, error) {
  try {
    logger?.error?.(message, error);
  } catch {
    // Logging must never turn a recoverable feature failure into a page-wide one.
  }
}

function reportFeatureFailure({ name, error, onFailure, logger }) {
  logFailure(logger, `[Listing bootstrap] ${name} 初始化失败；其余模块将继续启动。`, error);
  try {
    onFailure?.(name, error);
  } catch (notificationError) {
    logFailure(logger, `[Listing bootstrap] ${name} 的故障提示未能显示。`, notificationError);
  }
}

/**
 * Start feature initializers independently, preserving their declared order.
 * Synchronous throws and rejected promises are reported without preventing the
 * remaining feature initializers from starting.
 *
 * @param {{name: string, initialize: () => unknown}[]} features
 * @param {{onFailure?: (name: string, error: unknown) => void, logger?: Console}} [options]
 */
export function startFeatureInitializers(features, { onFailure, logger = globalThis.console } = {}) {
  for (const feature of features || []) {
    const name = String(feature?.name || '未命名模块');
    const initialize = feature?.initialize;

    if (typeof initialize !== 'function') {
      reportFeatureFailure({
        name,
        error: new TypeError('initialize 必须是函数'),
        onFailure,
        logger,
      });
      continue;
    }

    try {
      const result = initialize();
      if (result && typeof result.then === 'function') {
        void Promise.resolve(result).catch((error) => {
          reportFeatureFailure({ name, error, onFailure, logger });
        });
      }
    } catch (error) {
      reportFeatureFailure({ name, error, onFailure, logger });
    }
  }
}
